// frontend/src/context/auth-context.tsx
import { createContext, useContext, useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import i18n, { LOCAL_STORAGE_LANG_KEY } from "@/i18n";
import { loginUser, logoutUser, getToken } from "../services/auth";
import { getMe, type UserProfile } from "@/services/users";
import { MAINTENANCE_LOCK, isEmailAllowed } from "@/utils/maintenance";
import { normalizeLanguage } from "@/locales/locales";


type User = {
  user_id: number;
  email: string;
  [key: string]: any;
  language?: "en" | "de";
};

type AuthContextType = {
  user: User | null;
  setUser: (u: any | null) => void;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isLoggedIn: boolean;
  isLoading: boolean;
};

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const hardLogout = (opts?: { stopLoading?: boolean }) => {
    logoutUser();      // clears token/localStorage
    setUser(null);     // clear React state
    if (opts?.stopLoading) setIsLoading(false); // only for bootstrap paths
  };

  const applyLanguage = async (lang?: string | null) => {
    const target = lang === "de" ? "de" : "en";
    if (i18n.language !== target) {
      await i18n.changeLanguage(target);
    }
    try {
      document.documentElement.setAttribute("lang", target);
      localStorage.setItem(LOCAL_STORAGE_LANG_KEY, target);
    } catch {}
  };

  useEffect(() => {
    const token = getToken();
    if (token && token !== "undefined") {
      try {
        const decoded = jwtDecode<User>(token);
        if (decoded && typeof decoded === "object") {
          if (MAINTENANCE_LOCK && !isEmailAllowed(decoded.email)) {
            hardLogout({ stopLoading: true });
            return;
          }          
          setUser(decoded);

          // ENRICH WITH /me AND APPLY LANGUAGE (prevents first-frame flip)
          (async () => {
            try {
              const me: UserProfile = await getMe();
              const lang = normalizeLanguage(me.language);
              const merged: User = { ...decoded, ...me, language: lang };
              setUser(merged);
              await applyLanguage(lang);
            } catch { // keep decoded user if /me fails
            } finally {
              setIsLoading(false);
            }
          })();
          return; // avoid falling through to the setIsLoading(false) below

        } else {
          throw new Error("Failed to decode token");
        }
      } catch (err) {
        console.warn("Invalid token removed:", err);
        hardLogout();
      }
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!MAINTENANCE_LOCK || !user) return;
    if (!isEmailAllowed(user.email)) {
      hardLogout();
    }
  }, [user]);

  const login = async (email: string, password: string): Promise<boolean> => {
    // Defense-in-depth to block programmatic logins during maintenance
    if (MAINTENANCE_LOCK && !isEmailAllowed(email)) {
      throw new Error(i18n.t("auth.login.maintenance_blocked"));
    }
    
    try {
      const result = await loginUser(email, password);
      if (result?.token) {
        const decoded = jwtDecode<User>(result.token);

        // Persist token and seed user immediately
        localStorage.setItem("token", result.token);
        localStorage.setItem("user", JSON.stringify(decoded));
        setUser(decoded);

        // Pull /me to get server-truth profile (incl. language) and apply i18n right away
        try {
          const me = await getMe();
          const lang = normalizeLanguage(me.language);
          const merged: User = { ...decoded, ...me, language: lang };
          setUser(merged);
          await applyLanguage(lang);
        } catch {
          // ignore; user stays decoded; language remains as-is
        }

        // Re-check maintenance lock after decode as well (belt & suspenders)
        if (MAINTENANCE_LOCK && !isEmailAllowed(decoded.email)) {
          hardLogout();
          throw new Error(i18n.t("auth.login.maintenance_blocked"));
        }
        return true;

      }
      // Defensive fallback
      throw new Error(i18n.t("auth.login.error"));
    } catch (err: any) {
      // Re-throw so pages can show precise backend message (e.g., 'Email not verified')
      throw err;
    }
  };

  const logout = () => {
    hardLogout();
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, isLoggedIn: !!user, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuthContext must be used within AuthProvider");
  return context;
};